import React, { useEffect, useState, useCallback } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  Loader2,
  RefreshCw,
  Users,
  Handshake,
  TrendingUp,
  Clock,
  Skull,
} from "lucide-react";
import { Button } from "../ui/button";
import { processService } from "../../services/process.service";
import { formatCurrencyBRL } from "../../lib/utils";

const ACEITE_COLORS = ["#6366f1", "#e2e8f0"];

function KpiCard({
  icon: Icon,
  label,
  value,
  sub,
  colorClass = "text-slate-700",
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col gap-2 shadow-sm">
      <div className="flex items-center gap-2 text-slate-400 text-sm">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <p className={`text-3xl font-bold ${colorClass}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mt-6 mb-3">
      {children}
    </h3>
  );
}

function FinancialRow({ label, value, highlight }) {
  return (
    <div
      className={`flex justify-between items-center py-2 border-b border-slate-100 last:border-0 ${highlight ? "font-semibold" : ""}`}
    >
      <span className="text-sm text-slate-600">{label}</span>
      <span
        className={`text-sm ${highlight ? "text-slate-800" : "text-slate-500"}`}
      >
        {formatCurrencyBRL(value)}
      </span>
    </div>
  );
}

const CustomTooltipCurrency = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 shadow text-sm">
      <p className="font-medium text-slate-700">{label?.replace("\n", " ")}</p>
      <p className="text-slate-500">{formatCurrencyBRL(payload[0].value)}</p>
    </div>
  );
};

const CustomTooltipCount = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 shadow text-sm">
      <p className="font-medium text-slate-700">{payload[0].name}</p>
      <p className="text-slate-500">{payload[0].value} substituídos</p>
    </div>
  );
};

export function DashboardTab({ processoId, stats: externalStats }) {
  const [fetchedStats, setFetchedStats] = useState(null);
  const [loading, setLoading] = useState(!externalStats);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (externalStats) return;
    setLoading(true);
    setError(null);
    try {
      const data = await processService.getDashboardStats(processoId);
      setFetchedStats(data);
    } catch (e) {
      setError("Não foi possível carregar o dashboard.");
    } finally {
      setLoading(false);
    }
  }, [processoId, externalStats]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = externalStats ?? fetchedStats;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando
        dashboard...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center py-24 gap-3 text-slate-500">
        <p>{error}</p>
        <Button variant="outline" onClick={load}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  const {
    total,
    total_acordo,
    total_cessao,
    total_sem_resposta,
    total_falecidos,
    acordo,
    cessao,
    fees,
  } = stats;

  const totalResponderam = total_acordo + total_cessao;
  const pctCessao =
    total > 0 ? ((total_cessao / total) * 100).toFixed(1) : "0.0";
  const pctResponderam =
    total > 0 ? ((totalResponderam / total) * 100).toFixed(1) : "0.0";

  const aceiteData = [
    { name: "Aceitaram o Acordo", value: totalResponderam },
    { name: "Sem resposta", value: total_sem_resposta },
  ].filter((d) => d.value > 0);

  const financialBarData = [
    {
      name: "Proposta\nTotal",
      value: acordo.proposta_total + cessao.proposta_total,
      fill: "#6366f1",
    },
    {
      name: "Crédito\nAdquirido",
      value: cessao.credito_total,
      fill: "#f59e0b",
    },
    {
      name: "Propostas\nAceitas",
      value: cessao.proposta_total,
      fill: "#22c55e",
    },
    {
      name: "Líquido\nAdquirido",
      value: cessao.liquido_total,
      fill: "#0ea5e9",
    },
  ];

  return (
    <div className="space-y-2 pb-8">
      <div className="flex justify-between items-center">
        <SectionTitle>Resumo</SectionTitle>
        {!externalStats && (
          <Button
            variant="ghost"
            size="icon"
            onClick={load}
            className="h-8 w-8 text-slate-400 hover:text-slate-600"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard icon={Users} label="Total de Substituídos" value={total} />
        <KpiCard
          icon={Handshake}
          label="Aceitaram o Acordo"
          value={totalResponderam}
          sub={`${pctResponderam}% do total`}
          colorClass="text-indigo-600"
        />
        <KpiCard
          icon={TrendingUp}
          label="Aceitaram a Cessão"
          value={total_cessao}
          sub={`${pctCessao}% do total`}
          colorClass="text-amber-600"
        />
        <KpiCard
          icon={Clock}
          label="Sem Resposta"
          value={total_sem_resposta}
          sub={`${total > 0 ? ((total_sem_resposta / total) * 100).toFixed(1) : 0}% do total`}
          colorClass="text-slate-400"
        />
        <KpiCard
          icon={Skull}
          label="Falecidos"
          value={total_falecidos}
          colorClass="text-slate-500"
        />
      </div>

      {/* Charts Row */}
      <SectionTitle>Distribuição</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Aceite Pie */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-600 mb-4">
            Tipo de Aceite
          </p>
          {total === 0 ? (
            <p className="text-slate-400 text-sm text-center py-8">Sem dados</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={aceiteData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {aceiteData.map((entry, i) => (
                    <Cell key={entry.name} fill={ACEITE_COLORS[i]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltipCount />} />
                <Legend
                  formatter={(value) => (
                    <span className="text-xs text-slate-600">{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
          <p className="text-xs text-center text-slate-400 mt-1">
            {pctResponderam}% responderam · dos quais {totalResponderam > 0 ? ((total_cessao / totalResponderam) * 100).toFixed(1) : "0.0"}% optaram pela cessão
          </p>
        </div>

        {/* Financial Detail cards */}
        <div className="flex flex-col gap-3">
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <p className="text-xs font-semibold text-indigo-600 mb-2">
              Consolidado ({total_acordo + total_cessao} substituídos)
            </p>
            <FinancialRow
              label="Crédito Bruto Total"
              value={acordo.credito_total + cessao.credito_total}
              highlight
            />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <p className="text-xs font-semibold text-amber-600 mb-2">
              Cessão ({total_cessao} substituídos)
            </p>
            <FinancialRow
              label="Crédito Bruto Total"
              value={cessao.credito_total}
            />
            <FinancialRow
              label="Proposta Final Total"
              value={cessao.proposta_total}
              highlight
            />
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-slate-600">Deságio Médio</span>
              <span className="text-sm text-slate-500">
                {cessao.desagio_medio.toFixed(2)}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Bar Chart */}
      <SectionTitle>Valores Consolidados</SectionTitle>
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <p className="text-sm font-medium text-slate-600 mb-4">
          Visão geral dos valores consolidados
        </p>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={financialBarData} barCategoryGap="35%">
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: "#64748b" }}
              axisLine={false}
              tickLine={false}
              interval={0}
            />
            <YAxis
              tickFormatter={(v) => `R$ ${(v / 1_000_000).toFixed(1)}M`}
              tick={{ fontSize: 11, fill: "#94a3b8" }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltipCurrency />} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {financialBarData.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className="flex flex-wrap gap-4 justify-center mt-2">
          {financialBarData.map((d) => (
            <div
              key={d.name}
              className="flex items-center gap-1.5 text-xs text-slate-500"
            >
              <span
                className="w-3 h-3 rounded-sm inline-block"
                style={{ backgroundColor: d.fill }}
              />
              {d.name.replace("\n", " ")}
            </div>
          ))}
        </div>
      </div>

      {/* Fee totals */}
      <SectionTitle>Taxas e Deduções</SectionTitle>
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <FinancialRow label="Honorários Sindicato (Total)" value={acordo.sindicato_total + cessao.sindicato_total} />
        <FinancialRow label="Honorários Contador (Total)" value={acordo.contador_total + cessao.contador_total} />
        <FinancialRow label="Honorários Sindicato (Apenas Cessão)" value={cessao.sindicato_total} />
        <FinancialRow label="Honorários Contador (Apenas Cessão)" value={cessao.contador_total} />
        {fees.total_fgts > 0 && (
          <FinancialRow label="Total FGTS (referência)" value={fees.total_fgts} />
        )}

        {/* Líquido Adquirido breakdown (cessão only) */}
        <div className="mt-2 pt-2 border-t border-dashed border-slate-200">
          <div className="flex justify-between items-center py-1.5">
            <span className="text-sm text-slate-500">Proposta Final (Cessão)</span>
            <span className="text-sm text-slate-500">{formatCurrencyBRL(cessao.proposta_total)}</span>
          </div>
          <div className="flex justify-between items-center py-1.5">
            <span className="text-sm text-slate-400 pl-2">− Sindicato (Cessão)</span>
            <span className="text-sm text-red-400">− {formatCurrencyBRL(cessao.sindicato_total)}</span>
          </div>
          <div className="flex justify-between items-center py-1.5">
            <span className="text-sm text-slate-400 pl-2">− Contador (Cessão)</span>
            <span className="text-sm text-red-400">− {formatCurrencyBRL(cessao.contador_total)}</span>
          </div>
          {cessao.fgts_total > 0 && (
            <div className="flex justify-between items-center py-1.5">
              <span className="text-sm text-slate-400 pl-2">− FGTS (Cessão)</span>
              <span className="text-sm text-red-400">− {formatCurrencyBRL(cessao.fgts_total)}</span>
            </div>
          )}
          <div className="flex justify-between items-center py-2 mt-1 border-t border-slate-200 font-semibold">
            <span className="text-sm text-slate-800">= Líquido Adquirido</span>
            <span className="text-sm text-sky-600">{formatCurrencyBRL(cessao.liquido_total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
